import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import ReactPlayer from "react-player";
import * as S from "../../css/VassComponent2.styles";
import { vassViewState } from "../../stores/vass/vassViewState";
import { useRecoilState, useSetRecoilState, useRecoilValue } from "recoil";
import useGetVassSeconds from "../../api/vass/useGetVassSeconds";
import { nowVassDetailState } from "../../stores/vass/nowVassDetailState";
import { prevVassDetailState } from "../../stores/vass/prevVassDetailState";
import { videoListState } from "../../stores/vass/videoListState";
import { focusCCTVUrlState } from "../../stores/vass/focusCCTVUrlState";
import { loginState } from "../../stores/loginState";
import Loading from "../Loading";
import { barcodeTrackingState } from "../../stores/barcode/barcodeTrackingState";
import { trackingValueState } from "../../stores/barcode/trackingValueState";
import CCTVEditModalComponent from "./CCTVEditModalComponent"; // CCTVEditModal 컴포넌트 추가
import { isInvoiceBarcodeState } from "../../stores/vass/isInvoiceBarcodeState";
import TrackingBlank from './TrackingBlank';
import ReactDOMServer from 'react-dom/server';

function VassComponent2(props) {
  // NOTE 상태 변수 선언
  const login = useRecoilValue(loginState); // 로그인 전역변수
  const [isInvoice, setIsInvoice] = useRecoilState(isInvoiceBarcodeState); // 운송장 관리 페이지에서 넘어왔을 때 체크 변수
  const trackingValue = useRecoilValue(trackingValueState); // 화물추적 운송장, 날짜정보
  const nowVassDetail = useRecoilValue(nowVassDetailState); // 현재 운송장 번호
  const prevVassDetail = useRecoilValue(prevVassDetailState); // 이전 운송장 번호
  const videoList = useRecoilValue(videoListState); // CCTV 데이터
  const trackingList = useRecoilValue(barcodeTrackingState); // 화물추적 데이터
  const setFocusCCTVUrl = useSetRecoilState(focusCCTVUrlState); // 현재 보고있는 URL
  const setVassView = useSetRecoilState(vassViewState); // 화면 레이아웃 설정
  const [searchTaskType, setSearchTaskType] = useState("전체"); // 업무구분
  const [searchInvoice, setSearchInvoice] = useState(""); // 운송장 번호 입력칸
  const [searchDate, setSearchDate] = useState(new Date().toISOString().slice(0, 10)); // 조회일자
  const [isCCTVEditModalOpen, setIsCCTVEditModalOpen] = useState(false); // CCTV 편집 모달 표시여부
  const [isLoading, setIsLoading] = useState(false); // 페이지 로딩 여부
  const [currentPage, setCurrentPage] = useState(1); // CCTV 페이지 변수 (기본 1번째 페이지)
  const itemsPerPage = 16; // CCTV 페이지당 동영상 수
  const playerRefs = useRef([]); // 동영상 플레이어 Ref 배열

  const startIndex = (currentPage - 1) * itemsPerPage; // 첫번째 CCTV 동영상
  const currentVideos = videoList.slice(startIndex, startIndex + itemsPerPage); // 현재 페이지에 해당하는 CCTV 동영상들
  const isCustomView = typeof props.renderCCTVView === "function";
  const totalPages = isCustomView ? 1 : Math.ceil(videoList.length / itemsPerPage); // CCTV 동영상의 총 페이지 수

  // TODO CCTV 동영상 호출 API
  const { mutateAsync: vassInvoiceListWithSeconds } = useGetVassSeconds(searchInvoice, searchDate, searchTaskType);

  // NOTE 페이지 로딩 시
  useEffect(() => {
    // 운송장 번호에 입력이 없고, 현재 운송장의 영상추적이 조회되어있을 때
    if(!searchInvoice && nowVassDetail.barcode){
      setSearchInvoice(nowVassDetail.barcode);
    }
    // 운송장관리에서 넘어왔을 경우
    if (trackingValue && isInvoice) {
      setSearchInvoice(trackingValue.barcode);
      setSearchDate(trackingValue.scanDate);
      setIsInvoice(false);

      setTimeout(() => {
        onClickSearchInvoice();
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // NOTE 페이지 변경
  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
  };

  // NOTE 전체 재생
  const handlePlayAll = () => {
    playerRefs.current.forEach((player) => {
      if (player) player.getInternalPlayer().play();
    });
  };

  // NOTE 전체 일시정지
  const handlePauseAll = () => {
    playerRefs.current.forEach((player) => {
      if (player) player.getInternalPlayer().pause();
    });
  };

  // NOTE 특정 CCTV 클릭 시
  const cctvFocus = (props) => {
    return () => {
      if (props) {
        setFocusCCTVUrl(props);
        setVassView("1");
      }
    };
  };

  // NOTE 운송장 번호 검색 시
  const onClickSearchInvoice = async () => {
    setIsLoading(true); // 로딩 시작
    try {
      await vassInvoiceListWithSeconds();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false); // 로딩 종료
    }
  };

  // NOTE 이전 운송장 번호 검색 시
  const onClickSearchPrevInvoice = async () => {
    setIsLoading(true); // 로딩 시작
    try {
      await setSearchInvoice(prevVassDetail);
      vassInvoiceListWithSeconds();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false); // 로딩 종료
    }
  };

  // NOTE 화물추적 배송상태 표기
  const getColor = (state) => {
    if (trackingList.trace_list && trackingList.trace_list.length > 0) {
      const latestTrace = trackingList.trace_list[trackingList.trace_list.length - 1];
      return latestTrace.state_nm.includes(state)
        ? { color: props.theme.teal1 }
        : {};
    }
    return {};
  };

  // NOTE 화물추적 배송상태 이미지 표기
  const getCheck = (state) => {
    if (trackingList.trace_list && trackingList.trace_list.length > 0) {
      const latestTrace = trackingList.trace_list[trackingList.trace_list.length - 1];
      return latestTrace.state_nm.includes(state) ? "Check" : "";
    }
    return "";
  };

  // NOTE 화물추적 blank 오픈 시
  const handleTracking = () => {
    // 이미 열린 빈 창이 있는 경우, 그 창으로 포커스 이동
    if (window.blankWindow && !window.blankWindow.closed) {
      window.blankWindow.focus();
      return;
    }
  
    // 빈 창 열기
    const newBlankWindow = window.open('', '_blank', 'width=600,height=1000');
  
    // JSX를 HTML 문자열로 변환
    const content = ReactDOMServer.renderToString(<TrackingBlank trackingList={trackingList}/>);
  
    // 빈 창의 문서에 내용 작성
    newBlankWindow.document.open();
    newBlankWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Blank Window</title>
          <style>
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              border: 1px solid black;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
            }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `);
    newBlankWindow.document.close();
  
    // 상태에 열린 빈 창의 참조 저장
    window.blankWindow = newBlankWindow;
  };

  // CCTV 위치 변경 모달 열기
  const openCCTVEditModal = () => {
    setIsCCTVEditModalOpen(true);
  };

  // CCTV 위치 변경 모달 닫기
  const closeCCTVEditModal = () => {
    setIsCCTVEditModalOpen(false);
  };

  // NOTE 엔터 키 입력 시
  const handleKeyDownEnter = (e) => {
    if (e.key === "Enter") {
      onClickSearchInvoice();
    }
  };

  return (
    <S.MainContainer>
      <S.MenuContainer onKeyDown={handleKeyDownEnter}>
        <S.CCTVEdit>
          <h3>운송장 조회</h3>
        </S.CCTVEdit>
        <S.SearchContainer>
          <p>조회일자</p>
          <input
            type="date"
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
          />
        </S.SearchContainer>
        <S.SearchContainer>
          <p>업무구분</p>
          <select
            value={searchTaskType}
            onChange={(e) => setSearchTaskType(e.target.value)}
          >
            <option value="전체">전체</option>
            <option value={login.company === "LOGEN" ? "60" : "21"}>{login.company === "LOGEN" ? "배송입고 (60)" : "도착 (21)"}</option>
            <option value={login.company === "LOGEN" ? "30" : "20"}>{login.company === "LOGEN" ? "집하출고 (30)" : "발송 (20)"}</option>
          </select>
        </S.SearchContainer>
        <S.SearchContainer>
          <p>운송장 번호</p>
          <input
            value={searchInvoice}
            placeholder="운송장번호 입력"
            onChange={(e) => setSearchInvoice(e.target.value)}
          />
        </S.SearchContainer>
        <S.SearchContainer>
          <p>스캔일시</p>
          <input
            disabled
            value={nowVassDetail?.scan_total_time}
            style={{ background: props.theme.white5 }}
          />
        </S.SearchContainer>
        <S.SearchButton onClick={onClickSearchInvoice}>검색</S.SearchButton>
        <hr />
        <S.SearchContainer>
          <p>이전 운송장 번호</p>
          <input
            disabled
            value={prevVassDetail}
          />
        </S.SearchContainer>
        <S.SearchContainer>
          <p>스캔일시</p>
          <input
            disabled
            value={prevVassDetail?.scan_total_time}
            style={{ background: props.theme.white5 }}
          />
        </S.SearchContainer>
        <S.SearchButton onClick={onClickSearchPrevInvoice}>검색</S.SearchButton>
        <S.SearchNavigate>
          <div>
            <img src={require(`../../assets/icons/delivery1${getCheck("터미널")}${props.theme.icon}.png`)}
              alt=""/>
          <p style={getColor("터미널")}>터미널</p>
          </div>
          <div>
            <img src={require(`../../assets/icons/delivery2${getCheck("배송입고")}${props.theme.icon}.png`)}
              alt=""/>
          <p style={getColor("배송입고")}>배송입고</p>
          </div>
          <div>
            <img src={require(`../../assets/icons/delivery3${getCheck("배송출고")}${props.theme.icon}.png`)}
              alt=""/>
          <p style={getColor("배송출고")}>배송출고</p>
          </div>
          <div>
          <img src={require(`../../assets/icons/delivery4${getCheck("배송완료")}${props.theme.icon}.png`)}
              alt=""/>
          <p style={getColor("배송완료")}>배송완료</p>
          </div>
        </S.SearchNavigate>
          {trackingList.length > 0 &&(
            <S.TrackingDiv>
              <h6>배송담당 : {trackingList.dv_tradesub_nm}</h6>
              <h6>배송상태 : {(trackingList.trace_list?.[trackingList.trace_list.length - 1]?.state_nm) || ""}</h6>
            </S.TrackingDiv>
          )}
        <S.SearchButton onClick={handleTracking}>이전 운송장 화물추적 상세</S.SearchButton>
        <hr />
        <S.CCTVEdit onClick={openCCTVEditModal}>CCTV 위치 변경</S.CCTVEdit>
      </S.MenuContainer>
      <S.VerticalLine />
      <S.CCTVContainer>
        <S.CCTVHeaderContainer>
          <h3>영상추적</h3>
          <S.ViewSelectContainer>
            <div className="view1" onClick={() => setVassView("1")} />
            <div className="view2" onClick={() => setVassView("2")} />
          </S.ViewSelectContainer>
        </S.CCTVHeaderContainer>
        {isLoading && <Loading />}
        <S.CCTVView>
          {/* NOTE 기본 CCTV 그리드 뷰 또는 커스텀 뷰 렌더 */}
          {isCustomView
            ? props.renderCCTVView({ playerRefs })
            : Array.from({ length: 16 }, (_, index) => (
                <S.CCTVDiv key={index}>
                  {index < currentVideos.length ? (
                    <div title={currentVideos[index]?.cam_name}>
                      <ReactPlayer
                        onClick={cctvFocus(currentVideos[index]?.stream_url)}
                        ref={(el) => (playerRefs.current[index] = el)}
                        url={currentVideos[index]?.stream_url}
                        width="100%"
                        height="100%"
                        controls={true}
                        muted={true}
                      />
                    </div>
                  ) : (
                    ""
                  )}
                </S.CCTVDiv>
              ))}
        </S.CCTVView>
        <S.CCTVControllerContainer>
          <S.CCTVController>
          <button onClick={handlePlayAll}>전체 재생</button>
          <span>
            <p
              onClick={() => {
                if (currentPage > 1) {
                  handlePageChange(currentPage - 1);
                }
              }}
              style={{ cursor: currentPage > 1 ? "pointer" : "default" }}
            >
              ←
            </p>
            {Array.from({ length: totalPages }, (_, index) => (
              <p
                key={index}
                onClick={() => handlePageChange(index + 1)}
                style={{
                  cursor: "pointer",
                  fontWeight: currentPage === index + 1 ? "bold" : "normal",
                }}
              >
                {index + 1}
              </p>
            ))}
            <p
              onClick={() => {
                if (currentPage < totalPages) {
                  handlePageChange(currentPage + 1);
                }
              }}
              style={{
                cursor: currentPage < totalPages ? "pointer" : "default",
              }}
            >
              →
            </p>
          </span>
            <button onClick={handlePauseAll}>전체 일시정지</button>
          </S.CCTVController>
        </S.CCTVControllerContainer>
      </S.CCTVContainer>
      <CCTVEditModalComponent isOpen={isCCTVEditModalOpen} onRequestClose={closeCCTVEditModal} props={props}/>
    </S.MainContainer>
  );
}

VassComponent2.propTypes = {
  // NOTE 테마 객체는 styled-components에서 색상과 아이콘 경로를 가져오는 용도
  theme: PropTypes.shape({
    white1: PropTypes.string,
    white2: PropTypes.string,
    white4: PropTypes.string,
    white5: PropTypes.string,
    teal1: PropTypes.string,
    icon: PropTypes.string,
  }).isRequired,
  // NOTE CCTV 뷰를 교체할 수 있는 커스텀 렌더 함수
  renderCCTVView: PropTypes.func,
};

export default VassComponent2;
